/*
 * 戒导航 · 下载中心 —— 文件类型映射注册表（P7c）
 * =============================================================================
 * 设计依据：doc/知识库与文件下载中心设计方案.md §7.2（P7.3 列规范 / P7.7 扩展性）。
 *
 * 职责：把「扩展名」或后端归一化类型键（DownloadDataController::fileTypeFor 的返回值）
 *   映射为 { 图标, 色调, 文案键 }，供列表 / 详情页表格的「类型」列与文件图标列复用。
 *
 * ★ 单一契约：类型键集合必须与后端 `DownloadDataController::fileTypeFor()` 逐字一致：
 *     folder / archive / pdf / doc / image / media / file（未知回落）。
 *   新增类型时「后端 fileTypeFor 的 $map + 本文件的 FILE_TYPES / EXT_MAP」各加一项即可。
 *
 * ★ 图标口径：FontAwesome 一律用 FA5 名（fas/far + fa-xxx），与主站/后台一致。
 * ★ 文案口径：labelKey 为 jiedh-downloads.forum.* 下的键后缀，禁硬编码。
 * ★ 色调：固定的品牌向色相（十六进制），配合 16% 透明底做徽标，深浅色主题下均可读。
 */
import app from 'flarum/forum/app';

/** 归一化类型键（与后端 fileTypeFor() 完全一致）。 */

/** 单个类型的展示定义。 */

/**
 * 类型注册表（键集合 = 后端归一化键集合）。
 * 新增类型只加一条即可（列表/详情页表格自动生效）。
 */
export const FILE_TYPES = {
  folder: {
    icon: 'fas fa-folder',
    tone: '#e0a12e',
    labelKey: 'type_folder'
  },
  archive: {
    icon: 'fas fa-file-archive',
    tone: '#b2652e',
    labelKey: 'type_archive'
  },
  pdf: {
    icon: 'fas fa-file-pdf',
    tone: '#d43b3b',
    labelKey: 'type_pdf'
  },
  doc: {
    icon: 'fas fa-file-alt',
    tone: '#2f6fbf',
    labelKey: 'type_doc'
  },
  image: {
    icon: 'fas fa-file-image',
    tone: '#2f9e6f',
    labelKey: 'type_image'
  },
  media: {
    icon: 'fas fa-file-video',
    tone: '#8250c4',
    labelKey: 'type_media'
  },
  file: {
    icon: 'fas fa-file',
    tone: '',
    labelKey: 'type_file'
  }
};

/**
 * 类型键的有序数组（P9 · R5 类型多选过滤的选项来源）。
 *
 * 顺序即下拉展示顺序（文件夹 → 压缩包 → PDF → 文档 → 图片 → 媒体 → 其他），
 * 与后端 DownloadDataController::TYPE_KEYS 保持同一集合，避免前后端选项漂移。
 */
export const TYPE_KEYS = Object.keys(FILE_TYPES);

/** 取全部类型键（供类型多选渲染；返回副本，避免调用方误改注册表）。 */
export function typeKeys() {
  return TYPE_KEYS.slice();
}

/** 扩展名 → 类型键（与后端 fileTypeFor() 的 $map 一一对应，双向下沉）。 */
const EXT_MAP = (() => {
  const groups = {
    folder: [],
    archive: ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'tgz'],
    pdf: ['pdf'],
    doc: ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'md', 'csv', 'epub', 'rtf', 'odt', 'ods', 'odp', 'json', 'xml'],
    image: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'],
    media: ['mp4', 'mov', 'avi', 'mkv', 'webm', 'mp3', 'wav', 'flac', 'ogg'],
    file: []
  };
  const out = {};
  Object.keys(groups).forEach(type => {
    groups[type].forEach(ext => {
      out[ext] = type;
    });
  });
  return out;
})();

/**
 * 扩展名 → 归一化类型键（前端兜底；主口径用后端返回的 fileType）。
 * 与后端 `fileTypeFor()` 同规则：未知 / 空扩展名回落 'file'。
 */
export function fileTypeFor(extension) {
  const ext = String(extension ?? '').trim().toLowerCase().replace(/^\./, '');
  return EXT_MAP[ext] ?? 'file';
}

/**
 * 纯文本类扩展名白名单（浏览器可直接当文本渲染的一组）。
 * 与 EXT_MAP 的 'doc' 组部分重叠，但这里只挑「浏览器原生能显示」的纯文本，
 * 不含 docx/xlsx/pptx/epub 等二进制文档。
 * ⚠️ 本数组与下方 canPreview() 是本文件的「预览能力」唯一事实来源，
 *   后端若要独立判定，需与 DownloadDataController 保持同一集合。
 */
const PREVIEW_TEXT_EXT = ['txt', 'md', 'csv', 'json', 'xml', 'log', 'ini', 'yml', 'yaml'];

/**
 * 该文件能否用本地浏览器「直接预览」（决定详情页是否给出「预览」按钮）。
 *
 * 判定口径（宁缺毋滥，只认主流浏览器内置渲染器能打开的类型）：
 *   · pdf   → 浏览器内置 PDF 阅读器；
 *   · image → <img> 可渲染的位图 / 矢量图；
 *   · media → <video>/<audio> 可播放的音视频；
 *   · 纯文本（txt/md/csv/json/xml…）→ 浏览器直接把字节当文本显示。
 * 其余类型（压缩包 / Office 二进制文档 / 未知扩展名）浏览器无法渲染 → 返回 false，
 * 详情页不显示预览入口（但仍可下载）。若未来接入在线文档预览服务（如 office-web-viewer），
 * 只需在此函数内扩展判定即可。
 *
 * @param extension 小写扩展名（可空）
 * @param fileType  后端归一化类型键（优先；为空时用扩展名兜底推断）
 */
export function canPreview(extension, fileType) {
  const type = fileType || fileTypeFor(extension);
  if (type === 'pdf' || type === 'image' || type === 'media') return true;
  const ext = String(extension ?? '').trim().toLowerCase().replace(/^\./, '');
  return PREVIEW_TEXT_EXT.indexOf(ext) !== -1;
}

/** 取类型定义（接受任意字符串键，未知键回落 'file'，保证渲染永不崩）。 */
export function fileTypeDef(key) {
  return FILE_TYPES[key ?? 'file'] ?? FILE_TYPES.file;
}

/** 取类型中文案（已本地化）。 */
export function fileTypeLabel(key) {
  // extract=true：直接取纯字符串。不要用 String(trans(...))——
  // trans 默认返回 Mithril children 数组，String(数组) 会用 ', ' 连接各元素，
  // 一旦文案里带占位符就会多出逗号（同 DownloadPage#ts 的修复原因）。
  return app.translator.trans('jiedh-downloads.forum.' + fileTypeDef(key).labelKey, {}, true);
}

/**
 * 扩展名的大写展示文本（如 'pdf' → 'PDF'；空则回落为空串）。
 * 用于「类型」列右侧的扩展名徽标（Windows 资源管理器的「类型」列风格）。
 */
export function extensionLabel(extension, fileType) {
  const ext = String(extension ?? '').trim().toUpperCase();
  if (ext) return ext;

  // 无扩展名时：文件夹行显示「资料集」，其余回落 FILE
  if (fileType === 'folder') return '';
  return 'FILE';
}
flarum.reg.add('jiedh-downloads', 'forum/utils/fileTypes', { FILE_TYPES: FILE_TYPES,TYPE_KEYS: TYPE_KEYS,typeKeys: typeKeys,fileTypeFor: fileTypeFor,canPreview: canPreview,fileTypeDef: fileTypeDef,fileTypeLabel: fileTypeLabel,extensionLabel: extensionLabel, });